import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import NavBar from './components/NavBar';
import SubjectList from './components/SubjectList';
import QuestionBankList from './components/QuestionBankList';
import QuestionAnswering from './components/QuestionAnswering';
import QuestionBankEditor from './components/QuestionBankEditor';
import { clearCache } from './utils/generateQuestionBanks';
import './App.css';

function App() {
  const [currentSubject, setCurrentSubject] = useState(null);
  const [currentBank, setCurrentBank] = useState(null);
  const [subjectName, setSubjectName] = useState('');
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    // 应用启动时清除所有缓存，确保获取最新数据
    console.log('🧹 应用启动，清除所有缓存...');
    clearCache();
    
    // 清除浏览器缓存相关的localStorage标记
    const cacheKeys = Object.keys(localStorage).filter(key => 
      key.includes('Refresh') || key.includes('Cache')
    );
    cacheKeys.forEach(key => localStorage.removeItem(key));
    
    console.log('✅ 缓存清除完成，将获取最新数据');
    
    // 从localStorage恢复上次的状态
    const savedSubject = localStorage.getItem('currentSubject');
    const savedBank = localStorage.getItem('currentBank');
    const savedSubjectName = localStorage.getItem('subjectName');
    
    if (savedSubject) {
      setCurrentSubject(savedSubject);
      setSubjectName(savedSubjectName || '');
    }
    
    if (savedBank) {
      try {
        setCurrentBank(JSON.parse(savedBank));
      } catch (e) {
        console.error('恢复题库数据失败:', e);
        localStorage.removeItem('currentBank');
      }
    }
  }, []);

  // 保存当前状态到localStorage
  useEffect(() => {
    if (currentSubject) {
      localStorage.setItem('currentSubject', currentSubject);
      localStorage.setItem('subjectName', subjectName);
    } else {
      localStorage.removeItem('currentSubject');
      localStorage.removeItem('subjectName');
    }
    
    if (currentBank) {
      localStorage.setItem('currentBank', JSON.stringify(currentBank));
    } else {
      localStorage.removeItem('currentBank');
    }
  }, [currentSubject, currentBank, subjectName]);

  const handleSelectSubject = (subjectId, name) => {
    console.log('选择学科:', subjectId, name);
    setCurrentSubject(subjectId);
    setSubjectName(name || subjectId);
    setCurrentBank(null);
    setShowEditor(false);
  };

  const handleSelectBank = (bank) => {
    console.log('选择题库:', bank);
    setCurrentBank(bank);
    setShowEditor(false);
  };

  const handleBackToSubjects = () => {
    console.log('返回学科列表');
    setCurrentSubject(null);
    setCurrentBank(null);
    setSubjectName('');
    setShowEditor(false);
  };

  const handleBackToBanks = () => {
    console.log('返回题库列表');
    setCurrentBank(null);
    setShowEditor(false);
  };

  const handleShowEditor = () => {
    console.log('显示编辑器');
    setShowEditor(true);
    setCurrentBank(null);
  };

  const handleBackFromEditor = () => {
    console.log('从编辑器返回');
    setShowEditor(false);
  };

  // 根据当前状态确定返回按钮的行为
  const handleBack = () => {
    if (showEditor) {
      handleBackFromEditor();
    } else if (currentBank) {
      handleBackToBanks();
    } else if (currentSubject) {
      handleBackToSubjects();
    }
  };

  // 确定导航栏标题
  const getTitle = () => {
    if (showEditor) {
      return '题库编辑器';
    } else if (currentBank) {
      return currentBank.title;
    } else if (currentSubject) {
      return subjectName;
    } else {
      return '学科知识库';
    }
  };

  // 确定是否显示编辑按钮
  const shouldShowEditButton = () => {
    return !showEditor && !currentBank;
  };

  return (
    <Router>
      <div className="app">
        <NavBar 
          onBack={currentSubject || showEditor ? handleBack : null}
          title={getTitle()}
          onEdit={shouldShowEditButton() ? handleShowEditor : null}
        />
        
        <div className="main-content">
          {showEditor ? (
            <QuestionBankEditor onBack={handleBackFromEditor} />
          ) : !currentSubject ? (
            <SubjectList onSelectSubject={handleSelectSubject} />
          ) : !currentBank ? (
            <QuestionBankList 
              selectedSubject={currentSubject} 
              onSelectBank={handleSelectBank}
            />
          ) : (
            <QuestionAnswering 
              bank={currentBank}
              onBack={handleBackToBanks}
            />
          )}
        </div>
      </div>
    </Router>
  );
}

export default App;